import React, { useState, useEffect, useRef } from 'react';
import { 
  Database,
  Search,
  Filter,
  Plus,
  MessageSquare,
  User,
  MapPin,
  Users,
  Calendar,
  Tag,
  Edit2,
  Trash2,
  Clock,
  AtSign,
  Send,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  Globe,
  Building,
  Music
} from 'lucide-react';
import { api } from '../utils/api';
import Modal from '../components/Modal';

const CRMPage = () => {
  const [contacts, setContacts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedContact, setExpandedContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const commentInputRef = useRef(null);

  const contactTypes = ['all', 'artist', 'venue', 'partner', 'crew', 'booker', 'media', 'other'];

  useEffect(() => {
    loadCRMData();
  }, []);

  const loadCRMData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [contactsRes, commentsRes] = await Promise.all([
        api.crm.getContacts(),
        api.crm.getComments()
      ]);
      setContacts(contactsRes.contacts || []);
      setComments(commentsRes.comments || []);
    } catch (error) {
      console.error('Error loading CRM data:', error);
      setError('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const getContactIcon = (type) => {
    switch(type) {
      case 'artist': return <Music className="text-purple-500" size={20} />;
      case 'venue': return <MapPin className="text-blue-500" size={20} />;
      case 'partner': return <Users className="text-green-500" size={20} />;
      case 'crew': return <User className="text-orange-500" size={20} />;
      case 'booker': return <Calendar className="text-pink-500" size={20} />;
      case 'media': return <Globe className="text-cyan-500" size={20} />;
      default: return <User className="text-[#888888]" size={20} />;
    }
  };

  const handleAddComment = async (contactId) => {
    if (!commentText.trim()) return;

    try {
      // Extract mentions
      const mentions = commentText.match(/@(\w+)/g)?.map(m => m.substring(1)) || [];
      
      await api.crm.addComment({
        contactId,
        text: commentText,
        mentions
      });
      
      setCommentText('');
      await loadCRMData();
      
      // Send notifications for mentions
      if (mentions.length > 0) {
        await api.maaike.createTask({
          type: 'email',
          title: 'Send mention notifications',
          description: `Send notifications to: ${mentions.join(', ')}`,
          data: {
            mentions,
            comment: commentText,
            contactName: contacts.find(c => c.id === contactId)?.name
          }
        });
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment');
    }
  };

  const handleCommentChange = (e) => {
    const text = e.target.value;
    setCommentText(text);
    
    // Check for @ mentions
    const lastAtIndex = text.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = text.substring(lastAtIndex + 1);
      const beforeAt = text.substring(0, lastAtIndex);
      const lastChar = beforeAt[beforeAt.length - 1];
      
      // Only show suggestions if @ is preceded by space or is at start
      if (lastAtIndex === 0 || lastChar === ' ') {
        const suggestions = contacts
          .filter(c => c.name.toLowerCase().includes(afterAt.toLowerCase()))
          .slice(0, 5);
        setMentionSuggestions(suggestions);
        setShowMentions(suggestions.length > 0);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleSelectMention = (contact) => {
    const lastAtIndex = commentText.lastIndexOf('@');
    const beforeAt = commentText.substring(0, lastAtIndex);
    const afterAt = commentText.substring(lastAtIndex + 1);
    const spaceIndex = afterAt.indexOf(' ');
    const restOfText = spaceIndex !== -1 ? afterAt.substring(spaceIndex) : '';
    
    setCommentText(`${beforeAt}@${contact.name} ${restOfText}`);
    setShowMentions(false);
    commentInputRef.current?.focus();
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || contact.type === filterType;
    return matchesSearch && matchesType;
  });

  const getContactComments = (contactId) => {
    return comments.filter(c => c.contactId === contactId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2 flex items-center gap-3">
            <Database className="text-[#D4A574]" size={32} />
            CRM System
          </h1>
          <p className="text-[#888888]">Manage relationships and track interactions</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Add Contact
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{contacts.length}</p>
          <p className="text-sm text-[#888888]">Total Contacts</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{contacts.filter(c => c.type === 'artist').length}</p>
          <p className="text-sm text-[#888888]">Artists</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{contacts.filter(c => c.type === 'venue').length}</p>
          <p className="text-sm text-[#888888]">Venues</p>
        </div>
        <div className="card-dark text-center py-4">
          <p className="text-2xl font-light">{comments.length}</p>
          <p className="text-sm text-[#888888]">Comments</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-[#888888]" size={20} />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark w-full pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="text-[#888888]" size={20} />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-dark"
          >
            {contactTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Recent Comments */}
      <div className="card-dark p-6">
        <h2 className="text-xl font-light mb-4 flex items-center gap-2">
          <MessageSquare className="text-[#D4A574]" size={24} />
          Recent Comments
        </h2>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {comments.slice(0, 5).map(comment => {
            const contact = contacts.find(c => c.id === comment.contactId);
            return (
              <div key={comment.id} className="flex items-start gap-3 p-3 bg-[#0A0A0A] rounded-lg">
                <div className="p-2 bg-[#1A1A1A] rounded">
                  <User size={16} className="text-[#888888]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm mb-1">
                    <span className="font-medium">{contact?.name}</span>
                    <span className="text-[#888888] ml-2">
                      {new Date(comment.createdAt).toLocaleString()}
                    </span>
                  </p>
                  <p className="text-sm text-[#CCCCCC]">{comment.text}</p>
                  {comment.mentions && comment.mentions.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {comment.mentions.map(mention => (
                        <span key={mention} className="text-xs text-[#D4A574]">
                          @{mention}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Contacts List */}
      <div className="space-y-4">
        {filteredContacts.map((contact) => (
          <div key={contact.id} className="card-dark">
            {/* Contact Header */}
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => setExpandedContact(expandedContact === contact.id ? null : contact.id)}
            >
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2 bg-[#0A0A0A] rounded-lg">
                  {getContactIcon(contact.type)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-medium mb-1">{contact.name}</h3>
                  <p className="text-sm text-[#888888] mb-2">
                    {contact.type.charAt(0).toUpperCase() + contact.type.slice(1)}
                    {contact.company && ` • ${contact.company}`}
                  </p>
                  {contact.tags && contact.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {contact.tags.map(tag => (
                        <span key={tag} className="text-xs px-2 py-1 bg-[#0A0A0A] rounded flex items-center gap-1">
                          <Tag size={12} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors">
                {expandedContact === contact.id ? <ChevronUp /> : <ChevronDown />}
              </button>
            </div>

            {/* Expanded Details */}
            {expandedContact === contact.id && (
              <div className="mt-6 pt-6 border-t border-[#333333] space-y-6 animate-slide-up">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Contact Info */}
                  <div>
                    <h4 className="text-sm font-medium text-[#888888] mb-3">Contact Information</h4>
                    <div className="space-y-2">
                      {contact.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail size={16} className="text-[#888888]" />
                          <a href={`mailto:${contact.email}`} className="hover:text-[#D4A574]">
                            {contact.email}
                          </a>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone size={16} className="text-[#888888]" />
                          <a href={`tel:${contact.phone}`} className="hover:text-[#D4A574]">
                            {contact.phone}
                          </a>
                        </div>
                      )}
                      {contact.website && (
                        <div className="flex items-center gap-2 text-sm">
                          <Globe size={16} className="text-[#888888]" />
                          <a href={contact.website} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4A574]">
                            {contact.website}
                          </a>
                        </div>
                      )}
                      {contact.company && (
                        <div className="flex items-center gap-2 text-sm">
                          <Building size={16} className="text-[#888888]" />
                          <span>{contact.company}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {contact.notes && (
                    <div>
                      <h4 className="text-sm font-medium text-[#888888] mb-3">Notes</h4>
                      <p className="text-sm">{contact.notes}</p>
                    </div>
                  )}
                </div>

                {/* Comments Section */}
                <div>
                  <h4 className="text-sm font-medium text-[#888888] mb-3">
                    Comments ({getContactComments(contact.id).length})
                  </h4>
                  
                  {/* Comment Input */}
                  <div className="mb-4 relative">
                    <div className="flex gap-2">
                      <input
                        ref={commentInputRef}
                        type="text"
                        value={commentText}
                        onChange={handleCommentChange}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !showMentions) {
                            handleAddComment(contact.id);
                          }
                        }}
                        placeholder="Add a comment... Use @ to mention someone"
                        className="input-dark flex-1"
                      />
                      <button
                        onClick={() => handleAddComment(contact.id)}
                        className="btn-primary p-3"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                    
                    {/* Mention Suggestions */}
                    {showMentions && (
                      <div className="absolute top-full mt-1 left-0 right-0 bg-[#1A1A1A] border border-[#333333] rounded-lg shadow-lg z-10">
                        {mentionSuggestions.map(suggestion => (
                          <button
                            key={suggestion.id}
                            onClick={() => handleSelectMention(suggestion)}
                            className="w-full text-left px-4 py-2 hover:bg-[#0A0A0A] flex items-center gap-2"
                          >
                            {getContactIcon(suggestion.type)}
                            <span>{suggestion.name}</span>
                            {suggestion.company && (
                              <span className="text-[#888888] text-sm">• {suggestion.company}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Comments List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {getContactComments(contact.id).map(comment => (
                      <div key={comment.id} className="bg-[#0A0A0A] p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs text-[#888888]">
                            {comment.author} • {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.text}</p>
                        {comment.mentions && comment.mentions.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {comment.mentions.map(mention => (
                              <span key={mention} className="text-xs text-[#D4A574]">
                                @{mention}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-[#333333]">
                  <button
                    onClick={() => setEditingContact(contact)}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button className="btn-secondary text-sm">
                    Add to Event
                  </button>
                  <button className="btn-secondary text-sm">
                    Email
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredContacts.length === 0 && (
        <div className="card-dark text-center py-12">
          <Database className="mx-auto text-[#333333] mb-4" size={48} />
          <p className="text-[#888888]">No contacts found</p>
        </div>
      )}
    </div>
  );
};

export default CRMPage;